import { useState } from 'react';

/**
 * Per-platform mock card. Renders how a draft would appear on the chosen
 * platform AND the chosen post type (carousel shows N images, article shows
 * a link card, video shows a play overlay, etc.).
 */
interface PreviewProps {
  platform: string;
  caption: string;
  postType: string;
  authorName: string;
  workspaceName: string;
  /** Real follower count from the connection; null/undefined → hide the line. */
  followerCount?: number | null;
  /** Real connection avatar; null/empty → fall back to initials. */
  avatarUrl?: string | null;
  viewport: 'desktop' | 'mobile';
  mediaUrls?: string[];
  heroEdit?: { rotate?: number; filter?: string };
  /** X poll (twitter + poll post type) — options + duration. */
  poll?: { options: string[]; durationMinutes: number };
  /** X quote reference — the quoted post's id (quote mode only). */
  xRef?: { mode: 'quote' | 'reply' | null; postId: string | null };
  /** X thread segments (thread post type) — ordered text + media per tweet. */
  thread?: { text: string; mediaUrls: string[] }[];
}

/** Human poll-duration affordance: "5m", "6h", "1d", "7d". */
const fmtPollDuration = (min: number) =>
  min < 60 ? `${min}m` :
  min < 1440 ? `${Math.round(min / 60)}h` :
  `${Math.round(min / 1440)}d`;

const fmtCount = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` :
  n >= 1_000 ? `${(n / 1_000).toFixed(0)},${String(n % 1000).padStart(3, '0')}` :
  String(n);

const FILTER_CSS: Record<string, string> = {
  none: 'none',
  grayscale: 'grayscale(1)',
  sepia: 'sepia(0.6) saturate(1.2)',
  contrast: 'contrast(1.3) saturate(1.2)',
  fade: 'brightness(1.1) saturate(0.7) contrast(0.95)',
};
const editStyleOf = (edit?: { rotate?: number; filter?: string }): React.CSSProperties => ({
  transform: edit?.rotate ? `rotate(${edit.rotate}deg)` : undefined,
  filter: edit?.filter ? FILTER_CSS[edit.filter] : undefined,
});

/** Post-type families — used to pick the right media layout. */
const isCarousel = (pt: string) => pt === 'carousel' || pt === 'feed-carousel' || pt === 'multi-image' || pt === 'photo';
const isVideo    = (pt: string) => pt === 'video' || pt === 'reels' || pt === 'stories' || pt === 'short' || pt === 'video-pin' || pt === 'video-tweet';
const isArticle  = (pt: string) => pt === 'article' || pt === 'link';
const isText     = (pt: string) => pt === 'text' || pt === 'community';
const isThread   = (pt: string) => pt === 'thread';

// ── primitives ──────────────────────────────────────────────────────────
/**
 * Avatar — renders the REAL connection avatar (`url`) when present, falling back
 * to generated initials when the url is null/empty OR the image fails to load.
 */
function Avatar({ name, bg = 'bg-violet-600', size = 10, url }: { name: string; bg?: string; size?: number; url?: string | null }) {
  const [broken, setBroken] = useState(false);
  const initials = name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase() || 'A';
  const px = `${size * 4}px`;
  if (url && !broken) {
    return (
      <img src={url} alt={name} loading="lazy" onError={() => setBroken(true)}
        style={{ width: px, height: px }}
        className="rounded-full object-cover flex-shrink-0 bg-stone-100" />
    );
  }
  return <span style={{ width: px, height: px }} className={`rounded-full ${bg} text-white flex items-center justify-center text-sm font-semibold flex-shrink-0`}>{initials}</span>;
}

function ImageStub({ aspect = 'aspect-video', url, edit, className = '' }: { aspect?: string; url?: string; edit?: { rotate?: number; filter?: string }; className?: string }) {
  if (url) {
    return (
      <div className={`${aspect} bg-stone-100 overflow-hidden ${className}`}>
        <img src={url} alt="" loading="lazy" style={editStyleOf(edit)}
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          className="w-full h-full object-cover transition-transform" />
      </div>
    );
  }
  return (
    <div className={`${aspect} bg-gradient-to-br from-violet-300 via-pink-300 to-amber-200 flex items-center justify-center ${className}`}>
      <span className="text-stone-700/70 text-xs font-medium">Image preview</span>
    </div>
  );
}

function PlayOverlay({ size = 12 }: { size?: number }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <span style={{ width: `${size * 4}px`, height: `${size * 4}px` }} className="bg-black/60 rounded-full flex items-center justify-center text-white text-2xl">▶</span>
    </div>
  );
}

function DotsIndicator({ n, active = 0 }: { n: number; active?: number }) {
  if (n <= 1) return null;
  return (
    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
      {Array.from({ length: Math.min(n, 10) }).map((_, i) =>
        <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === active ? 'bg-white' : 'bg-white/50'}`} />
      )}
    </div>
  );
}

/** N-image grid that matches each platform's real-life carousel layout. */
function MediaGrid({ urls, count = 4, edit, aspect = 'aspect-video' }: { urls: string[]; count?: number; edit?: any; aspect?: string }) {
  const slots = Array.from({ length: count }, (_, i) => urls[i]);
  const visible = slots.length;
  if (visible === 1) return <ImageStub aspect={aspect} url={slots[0]} edit={edit} />;
  // X-style 2×2 / L-shape / split layouts. The wrapper sets the aspect; children fill.
  const cls = visible === 2 ? 'grid grid-cols-2 gap-0.5'
            : visible === 3 ? 'grid grid-cols-2 grid-rows-2 gap-0.5'
            : 'grid grid-cols-2 grid-rows-2 gap-0.5';
  return (
    <div className={`${aspect} ${cls}`}>
      {slots.map((u, i) => (
        <div key={i} className={`relative overflow-hidden bg-stone-100 ${visible === 3 && i === 0 ? 'row-span-2' : ''}`}>
          {u
            ? <img src={u} alt="" style={editStyleOf(i === 0 ? edit : undefined)} className="w-full h-full object-cover"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
            : <div className="w-full h-full bg-gradient-to-br from-violet-200 via-pink-200 to-amber-100" />}
        </div>
      ))}
    </div>
  );
}

/** Carousel viewer: one big image + counter chip + thumbnail strip. Used by LinkedIn carousel. */
function CarouselFrame({ urls, edit, aspect = 'aspect-video' }: { urls: string[]; edit?: any; aspect?: string }) {
  const count = Math.max(urls.length, 4);   // show at least 4 slots so the "carousel" feel is clear
  const visible = Array.from({ length: count }, (_, i) => urls[i]);
  return (
    <div>
      <div className={`relative ${aspect} bg-stone-100 overflow-hidden`}>
        {visible[0]
          ? <img src={visible[0]} alt="" style={editStyleOf(edit)} className="absolute inset-0 w-full h-full object-cover" />
          : <div className="absolute inset-0 bg-gradient-to-br from-violet-300 via-pink-300 to-amber-200 flex items-center justify-center text-stone-700/70 text-xs font-medium">Slide 1</div>}
        <span className="absolute top-2 right-2 px-2 py-0.5 bg-black/60 text-white text-[10px] rounded">1 / {count}</span>
        <span className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center">‹</span>
        <span className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center">›</span>
      </div>
      <div className="flex gap-1 mt-1">
        {visible.slice(0, 5).map((u, i) => (
          <div key={i} className={`flex-1 aspect-square bg-stone-100 overflow-hidden rounded ${i === 0 ? 'ring-2 ring-violet-500' : 'opacity-60'}`}>
            {u
              ? <img src={u} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full bg-gradient-to-br from-violet-200 via-pink-200 to-amber-100" />}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Link card — LinkedIn article / Facebook link / X URL-card style. */
function LinkCard({ url, title, description, brand = 'bg-blue-600' }: { url?: string; title: string; description: string; brand?: string }) {
  return (
    <div className="border border-stone-200 rounded-lg overflow-hidden">
      <ImageStub aspect="aspect-video" url={url} className="bg-stone-50" />
      <div className="p-3 bg-stone-50">
        <p className="text-[10px] uppercase tracking-wider text-stone-500">acmecorp.com</p>
        <p className="text-sm font-semibold text-stone-900 mt-0.5 line-clamp-2">{title}</p>
        <p className="text-[11px] text-stone-500 mt-1 line-clamp-2">{description}</p>
        <span className={`inline-block mt-2 px-2 py-0.5 text-[10px] text-white rounded ${brand}`}>Read more</span>
      </div>
    </div>
  );
}

/** 9:16 vertical frame, used by TikTok / Shorts / IG reels & stories / Pinterest video. */
function VerticalFrame({ heroUrl, edit, children }: { heroUrl?: string; edit?: any; children: React.ReactNode }) {
  return (
    <div className="relative rounded-2xl overflow-hidden bg-stone-900 mx-auto" style={{ aspectRatio: '9 / 16', maxWidth: 320 }}>
      {heroUrl
        ? <img src={heroUrl} alt="" style={editStyleOf(edit)} className="absolute inset-0 w-full h-full object-cover" />
        : <div className="absolute inset-0 bg-gradient-to-br from-pink-500 via-fuchsia-500 to-amber-400" />}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
      {children}
    </div>
  );
}

// ── main ────────────────────────────────────────────────────────────────
export function PlatformPreview({ platform, caption, postType, authorName, workspaceName, followerCount, avatarUrl, viewport, mediaUrls = [], heroEdit, poll, xRef, thread }: PreviewProps) {
  const isMobile = viewport === 'mobile';
  const container = `${isMobile ? 'max-w-[360px]' : 'max-w-full'} mx-auto`;
  const heroUrl = mediaUrls[0];
  // The connected account's display name drives the author name + handles on
  // every card (not the workspace/user). Handles derive from it.
  const displayName = authorName || workspaceName;
  const handle = displayName.toLowerCase().replace(/\s/g, '');
  const wsHandle = displayName.toLowerCase().replace(/\s/g, '_');
  // Real follower count (null/undefined ⇒ hide the line — never fake 42,134).
  const hasFollowers = typeof followerCount === 'number';

  // ── LinkedIn ─────────────────────────────────────────────
  if (platform === 'linkedin') {
    return (
      <div className={`${container} bg-white border border-stone-200 rounded-xl overflow-hidden`}>
        <div className="p-4 flex items-start gap-3">
          <Avatar name={displayName} bg="bg-blue-600" url={avatarUrl} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-stone-900 truncate">{displayName}</p>
            <p className="text-[11px] text-stone-500">{hasFollowers ? `${fmtCount(followerCount as number)} followers · ` : ''}Now · 🌐</p>
          </div>
        </div>
        <div className="px-4 pb-3"><p className="text-sm text-stone-800 whitespace-pre-wrap">{caption || <span className="text-stone-400">Your post text will appear here…</span>}</p></div>

        {/* LinkedIn's six API post types render distinctly (see DEV-LINKEDIN §5.2). */}
        {postType === 'multi-image' && (
          <div className="px-2 pb-2"><MediaGrid urls={mediaUrls} count={Math.min(9, Math.max(2, mediaUrls.length || 4))} edit={heroEdit} aspect="aspect-video" /></div>
        )}
        {postType === 'document' && (
          <div className="px-2 pb-2"><CarouselFrame urls={mediaUrls} edit={heroEdit} aspect="aspect-[4/3]" /></div>
        )}
        {postType === 'link' && (
          <div className="px-4 pb-3"><LinkCard url={heroUrl} title={(caption || 'Shared link').split('\n')[0].slice(0, 80)} description="acmecorp.com" /></div>
        )}
        {postType === 'video' && (
          <div className="relative"><ImageStub url={heroUrl} edit={heroEdit} /><PlayOverlay /></div>
        )}
        {postType === 'single-image' && <ImageStub url={heroUrl} edit={heroEdit} />}
        {/* 'text' → no media block */}

        <div className="px-4 py-2 border-t border-stone-100 flex items-center justify-between text-[11px] text-stone-500">
          <span>👍 ❤️ 💡  86</span><span>12 comments · 4 reposts</span>
        </div>
        <div className="px-2 py-1 flex items-center justify-around border-t border-stone-100 text-xs text-stone-600">
          {['👍 Like','💬 Comment','🔁 Repost','✉ Send'].map((s) => <span key={s} className="px-2 py-2">{s}</span>)}
        </div>
      </div>
    );
  }

  // ── X / Twitter ───────────────────────────────────────────
  if (platform === 'twitter') {
    return (
      <div className={`${container} bg-white border border-stone-200 rounded-xl p-4`}>
        <div className="flex items-start gap-3">
          <Avatar name={displayName} bg="bg-stone-900" url={avatarUrl} />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-1 flex-wrap">
              <span className="text-sm font-semibold text-stone-900">{displayName}</span>
              <span className="text-[11px] text-stone-500">@{handle} · now</span>
            </div>
            <p className="text-sm text-stone-800 mt-1 whitespace-pre-wrap">{caption || <span className="text-stone-400">Your tweet…</span>}</p>

            {/* media slots */}
            {(postType === 'single-image' || postType === 'multi-image' || postType === 'text-image') && mediaUrls.length > 0 && (
              <div className="mt-2 rounded-2xl overflow-hidden border border-stone-200">
                <MediaGrid urls={mediaUrls} count={Math.min(4, mediaUrls.length)} edit={heroEdit} aspect="aspect-video" />
              </div>
            )}
            {isVideo(postType) && (
              <div className="mt-2 rounded-2xl overflow-hidden border border-stone-200 relative">
                <ImageStub url={heroUrl} edit={heroEdit} />
                <PlayOverlay />
              </div>
            )}
            {postType === 'gif' && (
              <div className="mt-2 rounded-2xl overflow-hidden border border-stone-200 relative">
                <ImageStub url={heroUrl} edit={heroEdit} />
                <span className="absolute bottom-2 left-2 px-1.5 py-0.5 bg-black/70 text-white text-[10px] font-bold rounded">GIF</span>
              </div>
            )}
            {postType === 'poll' && (
              <div className="mt-2 space-y-1.5">
                {(poll?.options ?? ['', '']).map((opt, i) => (
                  <div key={i} className="border border-stone-300 rounded-full px-3 py-1.5 text-[13px] text-stone-700 flex items-center justify-between">
                    <span className="truncate">{opt.trim() || <span className="text-stone-400">Choice {i + 1}</span>}</span>
                    <span className="text-stone-400 text-[11px]">0%</span>
                  </div>
                ))}
                <p className="text-[11px] text-stone-500 mt-1">0 votes · {fmtPollDuration(poll?.durationMinutes ?? 1440)} left</p>
              </div>
            )}
            {isThread(postType) && (
              thread && thread.length > 0 ? (
                <div className="mt-2 border-l-2 border-stone-200 pl-3 space-y-3">
                  {thread.map((seg, i) => (
                    <div key={i}>
                      <p className="text-[10px] font-semibold text-stone-400">{i + 1}/{thread.length}</p>
                      <p className="text-sm text-stone-800 whitespace-pre-wrap mt-0.5">
                        {seg.text.trim() || <span className="text-stone-400">Tweet {i + 1}…</span>}
                      </p>
                      {seg.mediaUrls.length > 0 && (
                        <div className="mt-1.5 rounded-xl overflow-hidden border border-stone-200">
                          <MediaGrid urls={seg.mediaUrls} count={Math.min(4, seg.mediaUrls.length)} aspect="aspect-video" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-2 border-l-2 border-stone-200 pl-3 text-[11px] text-stone-500">
                  <p>1/3 — continue thread below…</p>
                  <p className="mt-1 text-stone-400">↳ 2/3 — your next tweet</p>
                  <p className="text-stone-400">↳ 3/3 — closing thought</p>
                </div>
              )
            )}
            {xRef?.mode === 'quote' && xRef.postId && (
              <div className="mt-2 border border-stone-200 rounded-2xl p-3">
                <div className="flex items-center gap-2">
                  <Avatar name="Quoted Author" bg="bg-stone-400" size={5} />
                  <span className="text-[12px] font-semibold text-stone-700">Quoted post</span>
                  <span className="text-[11px] text-stone-400">· #{xRef.postId}</span>
                </div>
                <p className="text-[12px] text-stone-500 mt-1.5">The post you're quoting will appear here.</p>
              </div>
            )}

            <div className="mt-3 flex items-center justify-between text-[11px] text-stone-500 max-w-[400px]">
              <span>💬 12</span><span>🔁 24</span><span>♡ 86</span><span>📊 1.2K</span><span>↗</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Instagram ─────────────────────────────────────────────
  if (platform === 'instagram') {
    // Reels & Stories are full-screen 9:16 experiences, not feed cards.
    if (postType === 'reels' || postType === 'stories') {
      const isStory = postType === 'stories';
      return (
        <div className={container}>
          <VerticalFrame heroUrl={heroUrl} edit={heroEdit}>
            {isStory ? (
              <>
                <div className="absolute top-2 left-2 right-2 flex gap-1">
                  {[1, 2, 3].map((i) => <div key={i} className={`h-0.5 flex-1 rounded ${i === 1 ? 'bg-white' : 'bg-white/40'}`} />)}
                </div>
                <div className="absolute top-4 left-3 right-3 flex items-center gap-2 text-white">
                  <span className="w-7 h-7 rounded-full border-2 border-white bg-white/20 flex items-center justify-center">
                    <Avatar name={workspaceName} bg="bg-transparent" size={5} url={avatarUrl} />
                  </span>
                  <span className="text-xs font-semibold">{wsHandle}</span>
                  <span className="text-[10px] opacity-80">now</span>
                  <span className="ml-auto opacity-80">⋯</span>
                </div>
                <div className="absolute left-3 right-3 bottom-4 text-center text-white">
                  <p className="text-sm font-medium drop-shadow line-clamp-3">{caption || <span className="opacity-60">Your story…</span>}</p>
                </div>
              </>
            ) : (
              <>
                <PlayOverlay />
                <div className="absolute top-3 left-3 px-2 py-0.5 rounded bg-white/15 text-white text-[10px] font-semibold">REELS</div>
                <div className="absolute right-2 bottom-20 flex flex-col items-center gap-3 text-white text-[10px]">
                  <Avatar name={workspaceName} bg="bg-stone-900" size={9} url={avatarUrl} />
                  <div className="flex flex-col items-center"><span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-lg">♡</span><span>86</span></div>
                  <div className="flex flex-col items-center"><span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-lg">💬</span><span>12</span></div>
                  <div className="flex flex-col items-center"><span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-lg">↗</span><span>Share</span></div>
                  <span className="mt-1 w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center text-lg animate-spin" style={{ animationDuration: '6s' }}>♪</span>
                </div>
                <div className="absolute left-3 right-16 bottom-3 text-white">
                  <p className="text-sm font-bold">{wsHandle}</p>
                  <p className="text-xs whitespace-pre-wrap line-clamp-3 opacity-95 mt-0.5">{caption || <span className="opacity-60">Your Reel…</span>}</p>
                  <p className="text-[10px] opacity-80 mt-1.5">♪ Original audio · {workspaceName}</p>
                </div>
              </>
            )}
          </VerticalFrame>
        </div>
      );
    }
    return (
      <div className={`${container} bg-white border border-stone-200 rounded-xl overflow-hidden`}>
        <div className="p-3 flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-gradient-to-tr from-amber-400 via-pink-500 to-purple-600 p-[2px] flex items-center justify-center">
            <span className="w-full h-full rounded-full bg-white flex items-center justify-center">
              <Avatar name={workspaceName} bg="bg-stone-900" size={6} url={avatarUrl} />
            </span>
          </span>
          <span className="text-sm font-semibold">{wsHandle}</span>
          <span className="ml-auto text-stone-400">⋯</span>
        </div>

        {postType === 'feed-carousel' ? (
          <div className="relative">
            <ImageStub aspect="aspect-square" url={heroUrl} edit={heroEdit} />
            <span className="absolute top-2 right-2 px-2 py-0.5 bg-black/60 text-white text-[10px] rounded">1 / {Math.max(mediaUrls.length, 4)}</span>
            <DotsIndicator n={Math.max(mediaUrls.length, 4)} />
          </div>
        ) : (
          <ImageStub aspect="aspect-square" url={heroUrl} edit={heroEdit} />
        )}

        <div className="px-3 py-2 flex items-center text-stone-700">
          <span className="mr-3 text-xl">♡</span>
          <span className="mr-3 text-xl">💬</span>
          <span className="mr-auto text-xl">↗</span>
          <span className="text-xl">🔖</span>
        </div>
        <div className="px-3 pb-3">
          <p className="text-xs font-semibold text-stone-800">{fmtCount(86)} likes</p>
          <p className="text-xs mt-1"><strong className="mr-1">{wsHandle}</strong>{caption || <span className="text-stone-400">Your caption…</span>}</p>
          <p className="text-[10px] text-stone-400 mt-1">View all 12 comments</p>
          <p className="text-[10px] text-stone-400">Just now</p>
        </div>
      </div>
    );
  }

  // ── Facebook ──────────────────────────────────────────────
  if (platform === 'facebook') {
    return (
      <div className={`${container} bg-white border border-stone-200 rounded-xl overflow-hidden`}>
        <div className="p-3 flex items-center gap-3">
          <Avatar name={workspaceName} bg="bg-blue-600" url={avatarUrl} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-stone-900">{workspaceName}</p>
            <p className="text-[11px] text-stone-500">Just now · 🌐</p>
          </div>
        </div>
        <div className="px-3 pb-2">
          <p className="text-sm text-stone-800 whitespace-pre-wrap">{caption || <span className="text-stone-400">Your post text…</span>}</p>
        </div>

        {isCarousel(postType) && mediaUrls.length > 0 && (
          <MediaGrid urls={mediaUrls} count={Math.min(4, mediaUrls.length || 4)} edit={heroEdit} aspect="aspect-[4/3]" />
        )}
        {isArticle(postType) && <div className="px-3 pb-3"><LinkCard url={heroUrl} title={(caption || 'Your article title').split('\n')[0].slice(0, 80)} description="acmecorp.com" /></div>}
        {!isText(postType) && !isCarousel(postType) && !isArticle(postType) && (
          <div className="relative"><ImageStub url={heroUrl} edit={heroEdit} />{isVideo(postType) && <PlayOverlay />}</div>
        )}

        <div className="px-3 py-1.5 border-t border-stone-100 flex items-center justify-between text-[11px] text-stone-500">
          <span>👍 ❤️ 86</span><span>12 comments · 4 shares</span>
        </div>
        <div className="px-3 py-2 border-t border-stone-100 flex items-center justify-around text-xs text-stone-600">
          <span>👍 Like</span><span>💬 Comment</span><span>↗ Share</span>
        </div>
      </div>
    );
  }

  // ── TikTok ────────────────────────────────────────────────
  if (platform === 'tiktok') {
    const isLive = postType === 'live';
    const isPhotoCarousel = postType === 'photo';
    return (
      <div className={container}>
        <VerticalFrame heroUrl={heroUrl} edit={heroEdit}>
          <div className="absolute top-3 left-0 right-0 flex justify-center text-white text-xs">
            <span className="opacity-70 mr-3">Following</span>
            <span className="font-semibold">For you</span>
          </div>
          {isLive && <span className="absolute top-3 left-3 px-2 py-0.5 bg-red-600 text-white text-[10px] rounded font-bold">● LIVE</span>}
          {isPhotoCarousel && <span className="absolute top-3 left-3 px-2 py-0.5 bg-white/20 text-white text-[10px] rounded">📷 1 / {Math.max(mediaUrls.length, 4)}</span>}

          <div className="absolute right-2 bottom-20 flex flex-col items-center gap-3 text-white text-[10px]">
            <Avatar name={workspaceName} bg="bg-stone-900" size={9} url={avatarUrl} />
            <div className="flex flex-col items-center -mt-2"><span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-lg">♡</span><span>342</span></div>
            <div className="flex flex-col items-center"><span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-lg">💬</span><span>18</span></div>
            <div className="flex flex-col items-center"><span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-lg">🔖</span><span>52</span></div>
            <div className="flex flex-col items-center"><span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-lg">↗</span><span>Share</span></div>
            <span className="mt-1 w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center text-lg animate-spin" style={{ animationDuration: '6s' }}>♪</span>
          </div>
          <div className="absolute left-3 right-16 bottom-3 text-white">
            <p className="text-sm font-bold">@{handle}</p>
            <p className="text-xs whitespace-pre-wrap line-clamp-3 opacity-95 mt-0.5">{caption || <span className="opacity-60">Your caption…</span>}</p>
            <p className="text-[10px] opacity-80 mt-1.5">{isLive ? '● LIVE NOW' : '♪ Original sound · ' + workspaceName}</p>
          </div>
        </VerticalFrame>
      </div>
    );
  }

  // ── YouTube ──────────────────────────────────────────────
  if (platform === 'youtube') {
    if (postType === 'short') {
      return (
        <div className={container}>
          <VerticalFrame heroUrl={heroUrl} edit={heroEdit}>
            <div className="absolute top-3 left-3 px-2 py-0.5 rounded bg-white/15 text-white text-[10px] font-semibold">SHORTS</div>
            <div className="absolute top-3 right-3 text-white text-lg">⋯</div>
            <div className="absolute right-2 bottom-24 flex flex-col items-center gap-4 text-white text-[10px]">
              <Avatar name={workspaceName} bg="bg-red-600" size={9} url={avatarUrl} />
              <div className="flex flex-col items-center"><span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-lg">👍</span><span>12K</span></div>
              <div className="flex flex-col items-center"><span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-lg">👎</span></div>
              <div className="flex flex-col items-center"><span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-lg">💬</span><span>340</span></div>
              <div className="flex flex-col items-center"><span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-lg">↗</span><span>Share</span></div>
            </div>
            <div className="absolute left-3 right-16 bottom-3 text-white">
              <p className="text-sm font-bold">@{handle}  <button className="ml-2 px-2.5 py-0.5 bg-red-600 rounded text-[10px] font-semibold">Subscribe</button></p>
              <p className="text-xs whitespace-pre-wrap line-clamp-3 opacity-95 mt-1">{caption || <span className="opacity-60">Your Short…</span>}</p>
            </div>
          </VerticalFrame>
        </div>
      );
    }
    if (postType === 'community') {
      return (
        <div className={`${container} bg-white border border-stone-200 rounded-xl p-4`}>
          <div className="flex items-center gap-3 mb-3">
            <Avatar name={workspaceName} bg="bg-red-600" url={avatarUrl} />
            <div className="min-w-0">
              <p className="text-sm font-semibold">{workspaceName}</p>
              <p className="text-[11px] text-stone-500">Just now</p>
            </div>
          </div>
          <p className="text-sm text-stone-800 whitespace-pre-wrap">{caption || <span className="text-stone-400">Your community post…</span>}</p>
          {heroUrl && <div className="mt-3 rounded-lg overflow-hidden border border-stone-200"><ImageStub url={heroUrl} edit={heroEdit} /></div>}
          <div className="mt-3 flex items-center gap-4 text-stone-500 text-xs">
            <span>👍 1.2K</span><span>👎</span><span>💬 86 Comments</span>
          </div>
        </div>
      );
    }
    // long-form video
    return (
      <div className={`${container} bg-white border border-stone-200 rounded-xl overflow-hidden`}>
        <div className="relative">
          <ImageStub url={heroUrl} edit={heroEdit} />
          <PlayOverlay />
          <span className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/80 text-white text-[10px] font-medium rounded">10:24</span>
        </div>
        <div className="p-3 flex gap-3">
          <Avatar name={workspaceName} bg="bg-red-600" size={9} url={avatarUrl} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-stone-900 line-clamp-2">{caption || <span className="text-stone-400">Your video title…</span>}</p>
            <p className="text-[11px] text-stone-500 mt-0.5">{workspaceName} · {fmtCount(12_400)} views · Just now</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Threads ───────────────────────────────────────────────
  if (platform === 'threads') {
    return (
      <div className={`${container} bg-white border border-stone-200 rounded-xl p-4`}>
        <div className="flex items-start gap-3">
          <Avatar name={workspaceName} bg="bg-stone-900" url={avatarUrl} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-stone-900">{wsHandle}</span>
              <span className="text-[11px] text-stone-400">5m</span>
              <span className="ml-auto text-stone-400">⋯</span>
            </div>
            <p className="text-sm text-stone-800 mt-1 whitespace-pre-wrap">{caption || <span className="text-stone-400">Your thread…</span>}</p>
            {postType === 'image' && <div className="mt-2 rounded-2xl overflow-hidden border border-stone-200"><ImageStub url={heroUrl} edit={heroEdit} /></div>}
            {postType === 'video' && (
              <div className="mt-2 rounded-2xl overflow-hidden border border-stone-200 relative">
                <ImageStub url={heroUrl} edit={heroEdit} />
                <PlayOverlay />
              </div>
            )}
            <div className="mt-3 flex items-center gap-5 text-stone-600 text-base">
              <span>♡</span><span>💬</span><span>↻</span><span>↗</span>
            </div>
            <p className="text-[11px] text-stone-400 mt-2">86 likes · 12 replies</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Pinterest ─────────────────────────────────────────────
  if (platform === 'pinterest') {
    const isIdea  = postType === 'idea-pin';
    const isVid   = postType === 'video-pin';
    return (
      <div className={container}>
        <div className="group relative rounded-2xl overflow-hidden mx-auto" style={{ aspectRatio: '2/3', maxWidth: 280 }}>
          {heroUrl
            ? <img src={heroUrl} alt="" style={editStyleOf(heroEdit)} className="absolute inset-0 w-full h-full object-cover" />
            : <div className="absolute inset-0 bg-gradient-to-br from-violet-400 via-pink-400 to-amber-300 flex items-center justify-center text-white/80 text-xs">Pin preview</div>}
          {isVid && <PlayOverlay />}
          {isIdea && (
            <div className="absolute top-2 left-2 right-2 flex gap-1">
              {[1,2,3,4].map((i) => <div key={i} className={`h-1 flex-1 rounded bg-white/40 ${i === 1 ? 'bg-white' : ''}`} />)}
            </div>
          )}
          <div className="absolute top-2 right-2">
            <button className="px-3 py-1.5 bg-red-600 text-white rounded-full text-xs font-semibold shadow-lg">Save</button>
          </div>
          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between opacity-0 group-hover:opacity-100">
            <span className="px-2 py-1 bg-white/90 rounded-full text-[10px] font-medium text-stone-700">{isVid ? '▶ Video' : isIdea ? '⊞ Idea' : 'Pin'}</span>
            <span className="px-2 py-1 bg-white/90 rounded-full text-[10px] text-stone-700">↗</span>
          </div>
        </div>
        <div className="mt-2 px-1 max-w-[280px] mx-auto">
          <p className="text-sm font-semibold text-stone-900 line-clamp-2">{caption || <span className="text-stone-400">Your Pin title…</span>}</p>
          <div className="flex items-center gap-2 mt-2">
            <Avatar name={workspaceName} bg="bg-red-500" size={6} url={avatarUrl} />
            <span className="text-xs text-stone-700">{workspaceName}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${container} bg-white border border-stone-200 rounded-xl p-4`}>
      <p className="text-sm text-stone-500">Preview for {platform} coming soon.</p>
    </div>
  );
}
